import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, Save, UploadCloud, Image as ImageIcon, Plus, Trash2,
  ChevronUp, ChevronDown, RotateCcw, ExternalLink,
} from "lucide-react";
import { cms, getToken, setToken, clearToken, resolveImg } from "@/lib/cms";
import { MediaPicker, MediaGrid } from "@/admin/MediaPicker";

/* ---------------- small field components ---------------- */
const Label = ({ children }) => (
  <label className="block text-[0.6rem] uppercase tracking-[0.3em] text-muted2 mb-2">{children}</label>
);
const inputCls = "w-full bg-ink border border-white/15 px-4 py-3 text-cream text-sm focus:outline-none focus:border-gold transition-colors";

function TextField({ label, value, onChange, area }) {
  return (
    <div className="mb-5">
      <Label>{label}</Label>
      {area ? (
        <textarea rows={4} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, allowRemove = true }) {
  const [pick, setPick] = useState(false);
  const isVideo = typeof value === "string" && /\.(mp4|mov|webm)(\?|$)/i.test(value);
  return (
    <div className="mb-5">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="w-28 h-20 bg-ink2 border border-white/15 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            isVideo ? <video src={resolveImg(value)} className="w-full h-full object-cover" />
              : <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" />
          ) : <ImageIcon size={20} className="text-muted2" />}
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={() => setPick(true)} className="text-xs uppercase tracking-[0.2em] border border-gold/50 text-gold px-4 py-2 hover:bg-gold hover:text-ink transition-colors">
            {value ? "Replace" : "Choose image"}
          </button>
          {allowRemove && value && (
            <button onClick={() => onChange("")} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300">Remove</button>
          )}
        </div>
      </div>
      <MediaPicker open={pick} onClose={() => setPick(false)} onSelect={(u) => { onChange(u); setPick(false); }} />
    </div>
  );
}

const card = "bg-ink2 border border-white/10 p-6 mb-6";
const btnGhost = "text-xs uppercase tracking-[0.2em] border border-white/20 px-4 py-2 hover:bg-white/10 transition-colors inline-flex items-center gap-2";

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await cms.login(email, password);
      setToken(data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <h1 className="font-serif text-4xl text-cream mb-2">Ghajoyan Studio</h1>
        <p className="text-muted2 text-sm mb-10 uppercase tracking-[0.3em] text-[0.6rem]">Content Manager</p>
        <Label>Email</Label>
        <input className={inputCls + " mb-5"} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
        <Label>Password</Label>
        <input type="password" className={inputCls + " mb-8"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
        <button disabled={busy} data-testid="admin-login-btn" className="w-full border border-gold text-gold py-4 text-xs uppercase tracking-[0.3em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Sign In
        </button>
      </form>
    </div>
  );
}

/* ---------------- Gallery editor ---------------- */
function GalleryEditor({ draft, setDraft }) {
  const [idx, setIdx] = useState(0);
  const cats = draft.categories || [];
  const cat = cats[idx];
  if (!cat) return null;

  const patchCat = (patch) => setDraft((d) => {
    const cs = [...d.categories];
    cs[idx] = { ...cs[idx], ...patch };
    return { ...d, categories: cs };
  });

  const setImages = (imgs) => patchCat({ images: imgs });
  const move = (i, dir) => {
    const imgs = [...(cat.images || [])];
    const j = i + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    setImages(imgs);
  };
  const removeImg = (i) => setImages(cat.images.filter((_, k) => k !== i));

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map((c, i) => (
          <button key={c.slug} onClick={() => setIdx(i)} data-testid={`gallery-tab-${c.slug}`}
            className={`text-xs uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${i === idx ? "border-gold text-gold" : "border-white/15 text-muted2 hover:text-cream"}`}>
            {c.title}
          </button>
        ))}
      </div>

      <div className={card}>
        <TextField label="Title" value={cat.title} onChange={(v) => patchCat({ title: v })} />
        <TextField label="Description" value={cat.blurb} onChange={(v) => patchCat({ blurb: v })} />
        <ImageField label="Cover image" value={cat.cover} onChange={(v) => patchCat({ cover: v })} allowRemove={false} />
      </div>

      {cat.isVideo ? (
        <div className={card}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-xl">Films & Reels</h4>
            <button onClick={() => patchCat({ videos: [...(cat.videos || []), { title: "New Film", src: "", poster: "" }] })} className={btnGhost}><Plus size={14} /> Add film</button>
          </div>
          {(cat.videos || []).map((v, i) => (
            <div key={i} className="border border-white/10 p-4 mb-4">
              <TextField label="Title" value={v.title} onChange={(val) => { const vs = [...cat.videos]; vs[i] = { ...vs[i], title: val }; patchCat({ videos: vs }); }} />
              <TextField label="Video URL (mp4)" value={v.src} onChange={(val) => { const vs = [...cat.videos]; vs[i] = { ...vs[i], src: val }; patchCat({ videos: vs }); }} />
              <ImageField label="Poster image" value={v.poster} onChange={(val) => { const vs = [...cat.videos]; vs[i] = { ...vs[i], poster: val }; patchCat({ videos: vs }); }} />
              <button onClick={() => patchCat({ videos: cat.videos.filter((_, k) => k !== i) })} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 inline-flex items-center gap-2"><Trash2 size={13} /> Remove film</button>
            </div>
          ))}
        </div>
      ) : (
        <div className={card}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-xl">Gallery images <span className="text-muted2 text-sm">({(cat.images || []).length})</span></h4>
            <AddImages onAdd={(urls) => setImages([...(cat.images || []), ...urls])} />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {(cat.images || []).map((img, i) => (
              <div key={i} className="group relative aspect-square bg-ink border border-white/10 overflow-hidden" data-testid={`gallery-img-${cat.slug}-${i}`}>
                <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ink/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button onClick={() => move(i, -1)} className="bg-white/15 hover:bg-white/30 p-1.5 rounded" title="Move left"><ChevronUp size={14} className="-rotate-90" /></button>
                  <button onClick={() => move(i, 1)} className="bg-white/15 hover:bg-white/30 p-1.5 rounded" title="Move right"><ChevronDown size={14} className="-rotate-90" /></button>
                  <button onClick={() => removeImg(i)} className="bg-red-600 p-1.5 rounded" title="Remove"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddImages({ onAdd }) {
  const [pick, setPick] = useState(false);
  return (
    <>
      <button onClick={() => setPick(true)} className={btnGhost} data-testid="gallery-add-image"><Plus size={14} /> Add image</button>
      <MediaPicker open={pick} onClose={() => setPick(false)} onSelect={(u) => { onAdd([u]); toast.success("Image added — keep adding or close"); }} />
    </>
  );
}

/* ---------------- List editors ---------------- */
function ReviewsEditor({ draft, setDraft }) {
  const reviews = draft.reviews || [];
  const patch = (i, key, val) => setDraft((d) => { const r = [...d.reviews]; r[i] = { ...r[i], [key]: val }; return { ...d, reviews: r }; });
  return (
    <div>
      <button onClick={() => setDraft((d) => ({ ...d, reviews: [{ name: "New Client", role: "Session", quote: "" }, ...(d.reviews || [])] }))} className={btnGhost + " mb-4"}><Plus size={14} /> Add review</button>
      {reviews.map((r, i) => (
        <div key={i} className={card}>
          <TextField label="Quote" value={r.quote} onChange={(v) => patch(i, "quote", v)} area />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Name" value={r.name} onChange={(v) => patch(i, "name", v)} />
            <TextField label="Role / Session" value={r.role} onChange={(v) => patch(i, "role", v)} />
          </div>
          <button onClick={() => setDraft((d) => ({ ...d, reviews: d.reviews.filter((_, k) => k !== i) }))} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 inline-flex items-center gap-2"><Trash2 size={13} /> Delete</button>
        </div>
      ))}
    </div>
  );
}

function FaqEditor({ draft, setDraft }) {
  const faqs = draft.faqs || [];
  const patch = (i, key, val) => setDraft((d) => { const f = [...d.faqs]; f[i] = { ...f[i], [key]: val }; return { ...d, faqs: f }; });
  return (
    <div>
      <button onClick={() => setDraft((d) => ({ ...d, faqs: [...(d.faqs || []), { q: "New question?", a: "" }] }))} className={btnGhost + " mb-4"}><Plus size={14} /> Add question</button>
      {faqs.map((f, i) => (
        <div key={i} className={card}>
          <TextField label="Question" value={f.q} onChange={(v) => patch(i, "q", v)} />
          <TextField label="Answer" value={f.a} onChange={(v) => patch(i, "a", v)} area />
          <button onClick={() => setDraft((d) => ({ ...d, faqs: d.faqs.filter((_, k) => k !== i) }))} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 inline-flex items-center gap-2"><Trash2 size={13} /> Delete</button>
        </div>
      ))}
    </div>
  );
}

function AboutEditor({ draft, setDraft }) {
  const a = draft.about || {};
  const set = (key, val) => setDraft((d) => ({ ...d, about: { ...d.about, [key]: val } }));
  const setVal = (i, val) => setDraft((d) => { const v = [...(d.about.values || [])]; v[i] = val; return { ...d, about: { ...d.about, values: v } }; });
  const setPara = (i, val) => setDraft((d) => { const p = [...(d.about.paragraphs || [])]; p[i] = val; return { ...d, about: { ...d.about, paragraphs: p } }; });
  return (
    <div className={card}>
      <TextField label="Section title (home)" value={a.title} onChange={(v) => set("title", v)} />
      <TextField label="Headline (about page)" value={a.headline} onChange={(v) => set("headline", v)} />
      <ImageField label="Portrait image" value={a.image} onChange={(v) => set("image", v)} allowRemove={false} />
      <TextField label="Intro paragraph 1" value={a.paragraphs?.[0]} onChange={(v) => setPara(0, v)} area />
      <TextField label="Intro paragraph 2" value={a.paragraphs?.[1]} onChange={(v) => setPara(1, v)} area />
      <TextField label="Philosophy" value={a.philosophy} onChange={(v) => set("philosophy", v)} area />
      <Label>Values</Label>
      <div className="grid sm:grid-cols-2 gap-3">
        {(a.values || []).map((v, i) => (
          <div key={i} className="flex gap-2">
            <input className={inputCls} value={v} onChange={(e) => setVal(i, e.target.value)} />
            <button onClick={() => setDraft((d) => ({ ...d, about: { ...d.about, values: d.about.values.filter((_, k) => k !== i) } }))} className="text-red-400 px-2"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setDraft((d) => ({ ...d, about: { ...d.about, values: [...(d.about.values || []), "New value"] } }))} className={btnGhost + " mt-3"}><Plus size={14} /> Add value</button>
    </div>
  );
}

function Inquiries() {
  const [items, setItems] = useState(null);
  useEffect(() => { cms.inquiries().then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-muted2 text-sm">Loading…</p>;
  if (!items.length) return <p className="text-muted2 text-sm">No inquiries yet.</p>;
  return (
    <div className="space-y-4">
      {items.map((q) => (
        <div key={q.id} className={card + " mb-0"}>
          <div className="flex justify-between flex-wrap gap-2">
            <h4 className="font-serif text-xl">{q.name} <span className="text-muted2 text-sm">· {q.session_type || "—"}</span></h4>
            <span className="text-[0.6rem] uppercase tracking-[0.2em] text-muted2">{new Date(q.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-gold mt-1">{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
          <p className="text-cream/80 mt-3 text-sm leading-relaxed">{q.message}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
const TABS = [
  ["hero", "Hero"], ["galleries", "Portfolio"], ["about", "About"],
  ["reviews", "Reviews"], ["faq", "FAQ"], ["contact", "Contact & Logo"],
  ["media", "Media Library"], ["inquiries", "Inquiries"],
];

function Dashboard({ onLogout }) {
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [hasUnpublished, setHasUnpublished] = useState(false);

  useEffect(() => {
    cms.getDraft().then((r) => { setDraft(r.data.draft); setHasUnpublished(r.data.has_unpublished); })
      .catch(() => { toast.error("Session expired"); onLogout(); });
  }, [onLogout]);

  const update = (fn) => { setDraft(fn); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try { await cms.saveDraft(draft); setDirty(false); setHasUnpublished(true); toast.success("Draft saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const publish = async () => {
    if (dirty) await cms.saveDraft(draft).catch(() => {});
    setPublishing(true);
    try { await cms.publish(); setDirty(false); setHasUnpublished(false); toast.success("Published! Your live site is updated."); }
    catch { toast.error("Publish failed"); }
    setPublishing(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset all content back to the original defaults? This affects your unpublished draft only until you publish.")) return;
    await cms.resetDefaults();
    const r = await cms.getDraft();
    setDraft(r.data.draft); setDirty(false); toast.success("Draft reset to defaults");
  };

  if (!draft) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;

  const setHero = (k, v) => update((d) => ({ ...d, hero: { ...d.hero, [k]: v } }));
  const setContact = (k, v) => update((d) => ({ ...d, contact: { ...d.contact, [k]: v } }));

  return (
    <div className="min-h-screen bg-ink text-cream">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink/90 backdrop-blur-xl border-b border-white/10 px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="font-serif text-xl">Ghajoyan Studio</span>
          {(dirty || hasUnpublished) && <span className="text-[0.6rem] uppercase tracking-[0.2em] text-gold border border-gold/40 px-2 py-1">{dirty ? "Unsaved changes" : "Unpublished changes"}</span>}
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="text-muted2 hover:text-cream text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"><ExternalLink size={13} /> View site</a>
          <button onClick={reset} className="text-muted2 hover:text-cream" title="Reset to defaults"><RotateCcw size={16} /></button>
          <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="text-xs uppercase tracking-[0.2em] border border-white/25 px-4 py-2.5 hover:bg-white/10 inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
          <button onClick={publish} disabled={publishing} data-testid="admin-publish-btn" className="text-xs uppercase tracking-[0.2em] bg-gold text-ink px-5 py-2.5 hover:bg-gold/90 inline-flex items-center gap-2 disabled:opacity-50 font-medium">
            {publishing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Publish
          </button>
          <button onClick={onLogout} className="text-muted2 hover:text-cream" title="Log out"><LogOut size={16} /></button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 flex gap-8">
        {/* sidebar */}
        <aside className="w-44 shrink-0">
          <nav className="sticky top-24 flex flex-col gap-1">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                className={`text-left text-xs uppercase tracking-[0.15em] px-3 py-3 border-l-2 transition-colors ${tab === id ? "border-gold text-gold" : "border-transparent text-muted2 hover:text-cream"}`}>
                {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* content */}
        <div className="flex-1 min-w-0">
          {tab === "hero" && (
            <div className={card}>
              <ImageField label="Hero background photo" value={draft.hero.image} onChange={(v) => setHero("image", v)} allowRemove={false} />
              <TextField label="Headline" value={draft.hero.headline} onChange={(v) => setHero("headline", v)} />
              <TextField label="Location line" value={draft.hero.subheadline} onChange={(v) => setHero("subheadline", v)} />
              <TextField label="Tagline (use a period to split into two lines)" value={draft.hero.description} onChange={(v) => setHero("description", v)} />
              <TextField label="Button text" value={draft.hero.buttonText} onChange={(v) => setHero("buttonText", v)} />
            </div>
          )}
          {tab === "galleries" && <GalleryEditor draft={draft} setDraft={update} />}
          {tab === "about" && <AboutEditor draft={draft} setDraft={update} />}
          {tab === "reviews" && <ReviewsEditor draft={draft} setDraft={update} />}
          {tab === "faq" && <FaqEditor draft={draft} setDraft={update} />}
          {tab === "contact" && (
            <div className={card}>
              <ImageField label="Logo" value={draft.logo} onChange={(v) => update((d) => ({ ...d, logo: v }))} allowRemove={false} />
              <TextField label="Location" value={draft.contact.location} onChange={(v) => setContact("location", v)} />
              <TextField label="Phone (display)" value={draft.contact.phone} onChange={(v) => setContact("phone", v)} />
              <TextField label="Phone (dial link, e.g. +18182558134)" value={draft.contact.phoneLink} onChange={(v) => setContact("phoneLink", v)} />
              <TextField label="Email" value={draft.contact.email} onChange={(v) => setContact("email", v)} />
              <TextField label="Instagram handle (without @)" value={draft.contact.instagram} onChange={(v) => setContact("instagram", v)} />
            </div>
          )}
          {tab === "media" && <div className={card}><MediaGrid /></div>}
          {tab === "inquiries" && <Inquiries />}
        </div>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    cms.me().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (authed === null) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} />;
}
